import { reactive, ref } from 'vue'
import { CAMERA_TYPES, cameraTypes, PAYLOAD_WIDE_IR } from '../types/device'
import store from '../store'
import { message } from 'ant-design-vue'
import { ROUTE_TYPE } from '../types/wayline'
import { state } from '../hooks/Cesium'
import { Cartesian3 } from 'cesium'

export const pauseMapKeyCodeEvt = ref(false)

// 类型定义
interface RouteData {
  leftExpandDis: number;
  rightExpandDis: number;
  allExpandDis: number;
  acquisitionType: string;
  imageFormat: string[];
  shootType: string;
  stripRouteType: number;
  autoFlightSpeed: number;
  heightMode: string;
  globalHeight: number;
  minGlobalHeight: number;
  GSD: number;
  irGSD: number;
  minGSD: number;
  minIrGSD: number;
  orthoLidarOverlapW: number;
  finishAction: string;
  flyToWaylineMode: string;
  takeOffSecurityHeight: number;
  routeDistance: number;
  centerLineDistance: number;
  duration: number;
  area: number;
  isUnite: boolean;
}

// 响应式数据
export const currentStripRouteSettingData = reactive<RouteData>({
  acquisitionType: '',
  leftExpandDis: 50,
  rightExpandDis: 50,
  allExpandDis: 50,
  imageFormat: ['wide', 'zoom'],
  shootType: 'time',
  stripRouteType: 1,
  autoFlightSpeed: 10,
  heightMode: 'EGM96',
  globalHeight: 100,
  minGlobalHeight: 100,
  GSD: 5,
  irGSD: 5,
  minGSD: 5,
  minIrGSD: 5,
  orthoLidarOverlapW: 70,
  finishAction: 'goHome',
  flyToWaylineMode: 'safely',
  takeOffSecurityHeight: 20,
  routeDistance: 0,
  centerLineDistance: 0,
  duration: 0,
  area: 0,
  isUnite: true,
})

// ---------- 纯函数工具 ----------
/**
 * 根据外扩距离计算 GSD（地面采样距离）
 * @param expandDistance 外扩距离（米）
 * @param payloadKey 载荷设备标识
 * @returns GSD 值与对应的红外 GSD
 */
function calculateGSDByExpandDistance (expandDistance: number, payloadKey: string) {
  const payload = PAYLOAD_WIDE_IR[payloadKey]
  if (!payload) return { GSD: 0, irGSD: 0 }

  const wideGSD = computeMr(payload.wideW, payload.wideF, payload.wideS, expandDistance)
  const irGSD = payload.irS ? computeMr(payload.irW, payload.irF, payload.irS, expandDistance) : 0

  return {
    GSD: Number(wideGSD.toFixed(1)),
    irGSD: Number(irGSD.toFixed(1)),
  }
}

/**
 * 计算 Mr 值（内部公式）
 */
function computeMr (C: number, x: number, l: number, m: number): number {
  const angle = 2 * Math.atan(C / (2 * x))
  return (m / Math.tan(angle / 2)) * (l / x) / 10
}

/**
 * 根据 GSD 计算飞行高度
 * @param gsd 目标 GSD（厘米/像素）
 * @param payloadKey 载荷标识
 * @param cameraType 相机类型（wide / ir）
 * @returns 高度（米）
 */
function getFlightHeightByGSD (gsd: number, payloadKey: string, cameraType: string): number {
  const payload = PAYLOAD_WIDE_IR[payloadKey]
  if (!payload) return 0

  const isIr = cameraType === cameraTypes.IR
  const sensorSize = (isIr ? payload.irS : payload.wideS) * 1e-4
  const focalLength = (isIr ? payload.irF : payload.wideF) * 0.1

  const height = (gsd * focalLength) / (sensorSize * 100)
  return Number(height.toFixed(1))
}

/**
 * 根据飞行高度计算 GSD
 * @param height 飞行高度（米）
 * @param payloadKey 载荷标识
 * @param cameraType 相机类型
 * @returns GSD（厘米/像素）
 */
function getGSDByFlightHeight (height: number, payloadKey: string, cameraType?: string): number {
  const payload = PAYLOAD_WIDE_IR[payloadKey]
  if (!payload) return 0

  const isIr = cameraType === cameraTypes.IR
  const sensorSize = (isIr ? payload.irS : payload.wideS) * 1e-4
  const focalLength = (isIr ? payload.irF : payload.wideF) * 0.1

  if (!sensorSize || !focalLength) return 0
  const gsd = (sensorSize * height * 100) / focalLength
  return Number(gsd.toFixed(1))
}

// ---------- 状态更新逻辑（依赖 store）----------
const getDefaultSettings = () => currentStripRouteSettingData
const getEditInfo = () => store.state.editInfo

/**
 * 根据外扩距离重新计算并更新最小 GSD、最小高度等限制值
 */
export function setExpandDis (): void {
  const settings = getDefaultSettings()
  const payloadType = getEditInfo().payload_types[0]
  if (!payloadType) return

  const { GSD, irGSD } = calculateGSDByExpandDistance(Number(settings.allExpandDis), payloadType)

  // 更新最小限制
  settings.minGSD = GSD
  settings.minIrGSD = irGSD
  settings.minGlobalHeight = getFlightHeightByGSD(GSD, payloadType, cameraTypes.WIDE)

  // 如果包含红外相机，则使用红外 GSD 重新计算
  if (settings.imageFormat.includes(cameraTypes.IR)) {
    const finalIrGSD = getGSDByFlightHeight(settings.minGlobalHeight, payloadType, cameraTypes.IR)
    settings.minGSD = finalIrGSD
    settings.minGlobalHeight = getFlightHeightByGSD(finalIrGSD, payloadType, cameraTypes.WIDE)
  }
}

/**
 * 根据 GSD、红外 GSD 或高度中的某一项，同步另外两项
 * @param source 基准类型：'GSD' | 'irGSD' | 'height'
 */
export function syncHeightAndGSD (source: 'GSD' | 'irGSD' | 'height'): void {
  const settings = getDefaultSettings()
  const payloadType = getEditInfo().payload_types[0]
  if (!payloadType) return

  switch (source) {
    case 'GSD':
      settings.globalHeight = getFlightHeightByGSD(settings.GSD, payloadType, CAMERA_TYPES.Wide)
      settings.irGSD = getGSDByFlightHeight(settings.globalHeight, payloadType, CAMERA_TYPES.Ir)
      break
    case 'irGSD':
      settings.globalHeight = getFlightHeightByGSD(settings.irGSD, payloadType, CAMERA_TYPES.Ir)
      settings.GSD = getGSDByFlightHeight(settings.globalHeight, payloadType, CAMERA_TYPES.Wide)
      break
    case 'height':
      settings.GSD = getGSDByFlightHeight(settings.globalHeight, payloadType, CAMERA_TYPES.Wide)
      settings.irGSD = getGSDByFlightHeight(settings.globalHeight, payloadType, CAMERA_TYPES.Ir)
      break
  }
}

/**
 * 显示最小限制警告
 */
function showMinLimitWarning (): void {
  message.info('当前外扩距离下，GSD/航高已达最小值！')
}

/**
 * 验证并修正 GSD / 红外 GSD / 高度，确保不低于最小值
 * @param type 要验证的类型
 */
export function validateMinLimit (type: 'GSD' | 'irGSD' | 'height'): void {
  const settings = getDefaultSettings()
  let needSync = false

  if (type === 'GSD' && settings.GSD < settings.minGSD) {
    settings.GSD = settings.minGSD
    needSync = true
  } else if (type === 'irGSD' && settings.irGSD < settings.minIrGSD) {
    settings.irGSD = settings.minIrGSD
    needSync = true
  } else if (type === 'height' && settings.globalHeight < settings.minGlobalHeight) {
    settings.globalHeight = settings.minGlobalHeight
    needSync = true
  }

  if (needSync) {
    syncHeightAndGSD(type)
    showMinLimitWarning()
  }
}

/**
 * 根据当前是否包含红外相机，校验相应的 GSD 限制
 */
export function setImageFormat (): void {
  const settings = getDefaultSettings()
  const hasIr = settings.imageFormat.includes(CAMERA_TYPES.Ir)
  validateMinLimit(hasIr ? 'irGSD' : 'GSD')
}

/**
 * 获取当前航线点并触发后续处理（原注释逻辑保留，需外部实现 Fe, ae, Yt）
 * @param points 航线点数组
 */
export function getNowPoint (points: any[]): void {
  store.state.stripLinePoint = points

  // 转换坐标格式
  const geographicPoints = points.map(point => [Number(point[0]), Number(point[1])])

  const settings = getDefaultSettings()
  const isBowRoute = settings.stripRouteType === ROUTE_TYPE.BOW_ROUTE
  const leftExpand = isBowRoute ? settings.leftExpandDis : settings.allExpandDis
  const rightExpand = isBowRoute ? settings.rightExpandDis : settings.allExpandDis

  const routeParams = {
    geographicPoints,
    leftExpand,
    rightExpand,
    gsd: settings.GSD,
    sideOverlap: settings.orthoLidarOverlapW,
    droneType: getEditInfo().payload_types[0],
    routeType: settings.stripRouteType,
  }

  console.log(routeParams)

  // TODO: 以下代码需要实现 Fe, ae, Yt 等函数，目前注释保留
  // Fe(routeParams).then(res => {
  //   i.stripAreaPoint = res.data[0].geographicPoints;
  //   i.stripAllRoute = res.data[1].geographicPoints;
  //   ae(i.stripAreaPoint);
  //   Yt(i.stripAllRoute);
  // });
}

/**
 * 根据外扩距离和负载类型获取最小允许的 GSD（示例算法，实际可能需后端接口）
 */
export function getMinGSDByExpand (expandDis, payloadType) {
  // 这里仅为示例逻辑，实际应根据专业公式或后端接口
  let baseGSD = 1.0
  if (expandDis < 50) baseGSD = 0.8
  else if (expandDis < 100) baseGSD = 1.2
  else baseGSD = 1.8

  if (payloadType === cameraTypes.IR) {
    return { GSD: baseGSD * 1.5, irGSD: baseGSD }
  } else {
    return { GSD: baseGSD, irGSD: baseGSD * 1.5 }
  }
}

/**
 * 根据 GSD 计算航高
 */
export function calcHeightByGSD (gsd, payloadType, lensType) {
  // 需要根据相机参数计算，此处返回模拟值
  return gsd * 100 // 示例：GSD 1cm/pixel 对应 100m 高度
}

/**
 * 根据航高计算 GSD
 */
export function calcGSDByHeight (height, payloadType, lensType) {
  return height / 100
}

/** 设置文件属性 */
export function setStripSettingData (data) {
  currentStripRouteSettingData.imageFormat = data.Document.Folder[0].payloadParam.imageFormat.split(',')
  currentStripRouteSettingData.shootType = data.Document.Folder[0].Placemark[0].shootType
  currentStripRouteSettingData.autoFlightSpeed = data.Document.Folder[0].autoFlightSpeed
  currentStripRouteSettingData.heightMode = data.Document.Folder[0].waylineCoordinateSysParam.heightMode
  currentStripRouteSettingData.globalHeight = data.Document.Folder[0].waylineCoordinateSysParam.globalShootHeight
  currentStripRouteSettingData.orthoLidarOverlapW = data.Document.Folder[0].Placemark[0].overlap.orthoCameraOverlapH
  currentStripRouteSettingData.finishAction = data.Document.missionConfig.finishAction
  currentStripRouteSettingData.flyToWaylineMode = data.Document.missionConfig.flyToWaylineMode
  currentStripRouteSettingData.leftExpandDis = currentStripRouteSettingData.allExpandDis = data.Document.Folder[0].Placemark[0].leftExtend
  currentStripRouteSettingData.rightExpandDis = data.Document.Folder[0].Placemark[0].rightExtend
  currentStripRouteSettingData.stripRouteType = data.Document.Folder[0].Placemark[0].singleLineEnable ? ROUTE_TYPE.ONE_ROUTE : ROUTE_TYPE.BOW_ROUTE
  if (currentStripRouteSettingData.stripRouteType === ROUTE_TYPE.ONE_ROUTE)setExpandDis()
}

function Yt (p) {
  // const a = state.viewer
  // const h = [store.state.routeStartPosition]
  // const w = ce(store.state.routeStartPosition)
  // h.push(jt({
  //   lng: w.lng,
  //   lat: w.lat,
  //   h: p[0][2]
  // })),
  // p.forEach(P => {
  //   h.push(jt({
  //     lng: P[0],
  //     lat: P[1],
  //     h: P[2]
  //   }))
  // }
  // ),
  // store.state.defaultStripRouteSettingData.routeDistance = Ot(h),
  // store.state.defaultStripRouteSettingData.duration = store.state.defaultStripRouteSettingData.routeDistance / store.state.defaultStripRouteSettingData.autoFlightSpeed
  // const k = []
  // i.stripLinePoint.forEach(P => {
  //   k.push(jt({
  //     lng: P[0],
  //     lat: P[1],
  //     h: P[2]
  //   }))
  // }
  // ),
  // store.state.defaultStripRouteSettingData.centerLineDistance = Ot(k),
  // et(a, `${store.state.editInfo.id}_route_line`),
  // Ke(a, `${store.state.editInfo.id}_route_line`, h)
}
function ae (p) {
  // const a = state.viewer
  // const h = []
  // p.forEach(w => {
  //   h.push(jt({
  //     lng: w[0],
  //     lat: w[1],
  //     h: w[2]
  //   }))
  // }
  // ),
  // store.state.defaultStripRouteSettingData.area = qe(h)
  // et(a, `${store.state.editInfo.id}_strip_route_area`)
  // Ae(a, h, `${store.state.editInfo.id}_strip_route_area`)
}

/** 获取航线长度 */
export const getPolylinePointLength = (list:Cartesian3[]) => {
  let length = 0
  for (let i = 1; i < list.length; i++) {
    const point1 = list[i - 1]
    const point2 = list[i]
    length += Cartesian3.distance(point1, point2)
  }
  return length
}
